import { useEffect, useState } from 'react';

import { Consent, ConsentType, getConsentsService } from '../api/consent';
import useSettings from './useSettings';
/**
 * Calls the "getConsentsService"-function to retrieve consents
 * @param consentType - a string that indicates which consent to retrieve
 * @returns an object containing:
 * consentDetails: a list of consents
 * isError: a boolean indicating whether an error occured during data fetching
 * isLoading: a boolean indicating whether the data is still being fetch
 */

const useGetConsentService = (consentType: ConsentType) => {
    const { apiKey, proxyBaseUrl } = useSettings();
    const [consentDetails, setConsentDetails] = useState<Consent[]>();
    const [isError, setIsError] = useState<boolean>(false);
    // Starts `true`, not `false`: a fetch is always about to happen, and `useEffect` runs after the first
    // paint, so a `false` initial value told every caller "loaded, and there are no consents" for the whole
    // first frame -- and nothing ever set it back to true. Callers that fail closed on a missing consent
    // (CoApplicantEskatKreditStatusConsentPageDK) depend on this being honest.
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        // Guards against writing state after the wizard has moved on: `Pages` unmounts each page on Next/Back, and
        // a fetch that resolves after that would otherwise call these setters on a component that no longer exists.
        let isMounted = true;

        setIsError(false);
        setIsLoading(true);
        getConsentsService(proxyBaseUrl, consentType, apiKey)
            .then((consents) => {
                if (isMounted) setConsentDetails(consents);
            })
            .catch(() => {
                if (isMounted) setIsError(true);
            })
            .finally(() => {
                if (isMounted) setIsLoading(false);
            });

        return () => {
            isMounted = false;
        };
        // Deliberately fetches once per mount, matching useGetKycService: consentType/apiKey/proxyBaseUrl are left
        // out of the dependency array on purpose -- re-running on a change to any of them would swap the consent
        // proposals out from under a form the applicant is already filling in. Every caller passes a literal
        // consentType, so there is nothing to refetch for; remounting this hook's owning page (the wizard router's
        // normal navigation) is the intended way to fetch a different consent. This is a deliberate omission, not
        // a missed dependency -- react-hooks/exhaustive-deps is off repo-wide, so this should not be re-flagged by
        // a future linter/bot.
    }, [setConsentDetails, setIsError]);

    return { consentDetails, isError, isLoading };
};
export default useGetConsentService;
